import {
  Button,
  CameraIcon,
  CameraOffIcon,
  CheckIcon,
  MicMutedIcon,
  MicUnmutedIcon,
  MouseDisabledIcon,
  ProfilePhoto,
  ScreenShareIcon,
  Text,
  UploadIcon,
  WarningOutlineIcon,
} from "@minervaproject/fds";
import { ReactNode } from "react";
import { Id, toast } from "react-toastify";

import { CaptionUnavailableIcon } from "./caption_unavailable_icon";

import { ConnectionIssueIcon } from "components/av/connection_issue_icon";
import { DISPLAY_BREAKOUT_ENDING_NOTIFICATION_AT_MS } from "components/shared/timers";
import { ProcessedChatMessage } from "components/sidebar/chat";
import { ChatType } from "shared/types";
import { RoleType, UserType } from "shared/types/user";
import { playNotificationAudio } from "utils/audio";
import { computeInitials, longDisplayName, profilePhotoUrl } from "utils/user";

import "./toasts.scss";

const AUTO_CLOSE_DURATION = 10 * 1000;
const SHORT_AUTO_CLOSE_DURATION = 5 * 1000;
const LONG_AUTO_CLOSE_DURATION = 25 * 1000;

const PREVIOUS_TOASTS: { [key: string]: Id } = {};

function dismissPreviousToast(key: string) {
  if (PREVIOUS_TOASTS[key]) {
    toast.dismiss(PREVIOUS_TOASTS[key]);
  }
}

function saveToastId(key: string, id: Id) {
  PREVIOUS_TOASTS[key] = id;
}

// Wrapper function to dismiss previous toast with the same key before creating a new toast
function toastWrapper(fn: () => Id) {
  function wrappedFunction(...keyParts: string[]) {
    const key = [fn.name, ...keyParts].join("-");
    dismissPreviousToast(key);
    const toastId = fn();
    saveToastId(key, toastId);
    return toastId;
  }
  return window.pageUnloadReason ? () => null : wrappedFunction;
}

function ToastBody({
  title,
  text,
  buttonText,
  buttonCallback,
  secondButtonText,
  secondButtonCallback,
}: {
  title: string;
  text: string | ReactNode;
  buttonText?: string;
  buttonCallback?: () => void;
  secondButtonText?: string;
  secondButtonCallback?: () => void;
}) {
  return (
    <>
      <Text kind="body" bold mb={1} color="blackTint20" data-testid="toast-title">
        {title}
      </Text>
      <Text kind="body-s" color="blackTint20" data-testid="toast-text">
        {text}
      </Text>
      <div className="flex">
        {buttonText && buttonCallback && (
          <Button kind="secondary-small" mt={4} onClick={buttonCallback} data-testid="toast-button">
            {buttonText}
          </Button>
        )}
        {secondButtonText && secondButtonCallback && (
          <Button kind="secondary-small" mt={4} ml={2} onClick={secondButtonCallback}>
            {secondButtonText}
          </Button>
        )}
      </div>
    </>
  );
}

function ToastIconWithProfilePhoto({ user, Icon = null }: { user: UserType; Icon?: ReactNode }) {
  return (
    <div className="relative">
      <ProfilePhoto initials={computeInitials(user)} size="lg" src={profilePhotoUrl(user)} />
      {Icon && (
        <div
          className="absolute circle bg-black-tint-20"
          style={{ width: "20px", height: "20px", bottom: "-2px", left: "-2px" }}
        >
          {Icon}
        </div>
      )}
    </div>
  );
}

function ToastIconWithBorder({
  Icon = null,
  borderColor = "var(--black-tint-70)",
}: {
  Icon?: ReactNode;
  borderColor?: string;
}) {
  if (!Icon) return null;

  return (
    <div
      className="circle bg-white relative"
      style={{ width: "50px", height: "50px", border: `2px solid ${borderColor}` }}
    >
      {Icon}
    </div>
  );
}

function createSharingNonScrollableResourceToast(title: string, message: string) {
  function _createSharingNonScrollableResourceToast() {
    return toast(<ToastBody title={title} text={message} />, {
      autoClose: 10 * 1000,
      hideProgressBar: true,
      icon: <MouseDisabledIcon width={54} height={54} viewBox="0 0 54 54" />,
    });
  }
  return toastWrapper(_createSharingNonScrollableResourceToast)(title, message);
}

function createBreakoutEndingSoonToast(handleOpenNotes: () => void) {
  function _createBreakoutEndingSoonToast() {
    playNotificationAudio();
    return toast(
      <ToastBody
        title="Breakouts Ending Soon"
        text="Your breakout will end soon. Would you like to open your notes in a new tab?"
        buttonText="Open Notes"
        buttonCallback={handleOpenNotes}
      />,
      {
        autoClose: DISPLAY_BREAKOUT_ENDING_NOTIFICATION_AT_MS,
        hideProgressBar: true,
        icon: <WarningOutlineIcon width={32} height={32} style={{ margin: "8px 11px" }} />,
      },
    );
  }
  return toastWrapper(_createBreakoutEndingSoonToast)();
}

function createErrorToast(title: string, text: string, doNotPlayAudio?: boolean) {
  function _createErrorToast() {
    if (!doNotPlayAudio) {
      playNotificationAudio();
    }
    return toast(<ToastBody title={title} text={text} />, {
      autoClose: AUTO_CLOSE_DURATION,
      hideProgressBar: true,
      icon: <WarningOutlineIcon width={32} height={32} style={{ margin: "8px 11px" }} />,
    });
  }
  return toastWrapper(_createErrorToast)(title, text);
}

function createDeviceToast(
  title: string,
  text: string,
  enabled: boolean,
  type: "camera" | "mic",
  handleButtonClick?: () => void,
  altButtonMessage?: string,
) {
  function _createDeviceToast() {
    let deviceIcon;
    if (type === "mic") {
      deviceIcon = enabled ? (
        <MicUnmutedIcon width={32} height={32} style={{ margin: "8px 11px" }} />
      ) : (
        <MicMutedIcon color="#B2261E" width={32} height={32} style={{ margin: "8px 11px" }} />
      );
    } else {
      deviceIcon = enabled ? (
        <CameraIcon width={32} height={32} style={{ margin: "8px 11px" }} />
      ) : (
        <CameraOffIcon color="#B2261E" width={32} height={32} style={{ margin: "8px 11px" }} />
      );
    }

    return toast(
      <ToastBody
        title={title}
        text={text}
        buttonCallback={handleButtonClick || undefined}
        buttonText={altButtonMessage || "AV Preferences"}
      />,
      {
        autoClose: AUTO_CLOSE_DURATION,
        hideProgressBar: true,
        icon: deviceIcon,
      },
    );
  }
  return toastWrapper(_createDeviceToast)(title, text, enabled ? "enabled" : "disabled", type);
}

function createDeviceErrorToast(device: "camera" | "mic", handleOpenAVPreferences?: () => void) {
  playNotificationAudio();
  return createDeviceToast(
    `${device === "camera" ? "Camera" : "Microphone"} Device Error`,
    `Your ${
      device === "camera" ? "camera" : "microphone"
    } has been disabled due to a technical issue`,
    false,
    device,
    handleOpenAVPreferences,
  );
}

function createFileUploadingToast(user: UserType, fileName: string) {
  function _createFileUploadingToast() {
    return toast(<ToastBody title="Uploading..." text={fileName} />, {
      autoClose: false,
      hideProgressBar: true,
      icon: (
        <ToastIconWithProfilePhoto
          user={user}
          Icon={<UploadIcon className="absolute-center" color="white" width={12} height={12} />}
        />
      ),
    });
  }
  return toastWrapper(_createFileUploadingToast)(user.id, fileName);
}

function createScreenshareRequestToast(user: UserType, handleAccept: () => void) {
  const title = "New Shared Screen";
  function _createScreenshareRequestToast() {
    playNotificationAudio();
    return toast(
      <ToastBody
        title={title}
        text={`${user.first_name} wishes to share their screen.`}
        buttonText="Display Screen"
        buttonCallback={handleAccept}
      />,
      {
        autoClose: AUTO_CLOSE_DURATION,
        hideProgressBar: true,
        icon: (
          <ToastIconWithProfilePhoto
            user={user}
            Icon={
              <ScreenShareIcon className="absolute-center" color="white" width={12} height={12} />
            }
          />
        ),
      },
    );
  }
  return toastWrapper(_createScreenshareRequestToast)(user.id, title);
}

function createFeatureResourceToast(
  user: UserType,
  isSelf: boolean,
  resourceTitle: string,
  handleFeatureResource: () => void,
) {
  function _createFeatureResourceToast() {
    playNotificationAudio();
    return toast(
      <ToastBody
        title={`${isSelf ? "You" : user.first_name} Uploaded a Resource`}
        text={resourceTitle}
        buttonText="Feature Resource"
        buttonCallback={handleFeatureResource}
      />,
      {
        autoClose: AUTO_CLOSE_DURATION,
        hideProgressBar: true,
        icon: (
          <ToastIconWithProfilePhoto
            user={user}
            Icon={<UploadIcon className="absolute-center" color="white" width={12} height={12} />}
          />
        ),
      },
    );
  }
  return toastWrapper(_createFeatureResourceToast)(user.id, resourceTitle);
}

function createResourceUploadedToast(user: UserType, resourceTitle: string) {
  function _createResourceUploadedToast() {
    return toast(<ToastBody title="You Uploaded a Resource" text={resourceTitle} />, {
      autoClose: AUTO_CLOSE_DURATION,
      hideProgressBar: true,
      icon: (
        <ToastIconWithProfilePhoto
          user={user}
          Icon={<UploadIcon className="absolute-center" color="white" width={12} height={12} />}
        />
      ),
    });
  }
  return toastWrapper(_createResourceUploadedToast)(user.id, resourceTitle);
}

function createMentionToast(
  chat: ChatType,
  users: { [id: string]: UserType },
  canMentionEveryone: boolean,
  onClick: undefined | (() => void),
) {
  function _createMentionToast() {
    const user = users[chat.user_id];
    playNotificationAudio();
    return toast(
      <ToastBody
        title={`${longDisplayName(user)}${
          user.role === RoleType.Captioner ? " (Captioner)" : ""
        } says`}
        text={
          <ProcessedChatMessage
            chat={chat}
            users={users}
            userCanMentionEveryoneInChat={canMentionEveryone}
          />
        }
      />,
      {
        autoClose: AUTO_CLOSE_DURATION,
        hideProgressBar: true,
        icon: <ToastIconWithProfilePhoto user={user} />,
        onClick,
      },
    );
  }
  return toastWrapper(_createMentionToast)(chat.id, chat.text);
}

function createMentionToastWithVisitGroupCta(
  chat: ChatType,
  users: { [id: string]: UserType },
  canMentionEveryone: boolean,
  onClick: undefined | (() => void),
  breakoutRoomTitle: string,
  visitGroupCallback: () => void,
) {
  function _createMentionToastWithVisitGroupCta() {
    const user = users[chat.user_id];
    playNotificationAudio();
    return toast(
      <ToastBody
        title={`${user.first_name} ${user.last_name}${
          user.role === RoleType.Captioner ? " (Captioner)" : ""
        } says`}
        text={
          <ProcessedChatMessage
            chat={chat}
            users={users}
            userCanMentionEveryoneInChat={canMentionEveryone}
          />
        }
        buttonText={`Visit Group ${breakoutRoomTitle}`}
        buttonCallback={visitGroupCallback}
      />,
      {
        autoClose: false,
        hideProgressBar: true,
        icon: <ToastIconWithProfilePhoto user={user} />,
        onClick,
      },
    );
  }
  return toastWrapper(_createMentionToastWithVisitGroupCta)(chat.id, chat.text);
}

function createRecordingToast(title: string, message: string) {
  function _createRecordingToast() {
    playNotificationAudio();
    return toast(<ToastBody title={title} text={message} />, {
      autoClose: SHORT_AUTO_CLOSE_DURATION,
      hideProgressBar: true,
      icon: (
        <ToastIconWithBorder
          Icon={<WarningOutlineIcon className="absolute-center" width={30} height={30} />}
        />
      ),
    });
  }
  return toastWrapper(_createRecordingToast)(title, message);
}

function createPoorConnectionToast(message: string) {
  playNotificationAudio();
  return toast(
    <ToastBody
      title="Connection Issue Detected"
      text={message}
      buttonText="Learn More"
      buttonCallback={() => {
        window.open(
          "https://help.minervaproject.com/en/articles/4296298-how-to-get-the-fastest-internet-connection",
          "_blank",
        );
        window.focus();
      }}
    />,
    {
      autoClose: LONG_AUTO_CLOSE_DURATION,
      hideProgressBar: true,
      icon: (
        <ToastIconWithBorder
          Icon={
            <ConnectionIssueIcon
              color="#B2261E"
              width={32}
              height={32}
              style={{ margin: "10px 10px" }}
              viewBox="0 0 12 12"
            />
          }
          borderColor="#B2261E"
        />
      ),
    },
  );
}

function createBugReportSubmittedToast() {
  function _createBugReportSubmittedToast() {
    return toast(<ToastBody title="Thank You" text="Your bug report has been submitted." />, {
      autoClose: SHORT_AUTO_CLOSE_DURATION,
      hideProgressBar: true,
      icon: (
        <ToastIconWithBorder
          Icon={<CheckIcon className="absolute-center" width={30} height={30} />}
        />
      ),
    });
  }
  return toastWrapper(_createBugReportSubmittedToast)();
}

function createNoCaptionsToast(shouldBeCaptioned: boolean) {
  function _createNoCaptionsToast() {
    const title = `Captions Not${shouldBeCaptioned ? " Yet" : ""} Available`;
    const text = shouldBeCaptioned
      ? "The captioner isn't in class yet. We'll show captions as soon as they're available."
      : "We've updated your settings, but there are no captions to display for this class.";

    playNotificationAudio();
    return toast(<ToastBody title={title} text={text} />, {
      autoClose: AUTO_CLOSE_DURATION,
      hideProgressBar: true,
      icon: (
        <ToastIconWithBorder
          Icon={
            <CaptionUnavailableIcon
              className="absolute-center"
              color="#B2261E"
              width={30}
              height={30}
              viewBox="0 0 30 30"
            />
          }
          borderColor="#B2261E"
        />
      ),
    });
  }
  return toastWrapper(_createNoCaptionsToast)();
}

function createCaptionerIsInAnotherRoomToast() {
  function _createCaptionerIsInAnotherRoomToast() {
    playNotificationAudio();
    return toast(
      <ToastBody
        title="Captioner Not In This Breakout Room"
        text="Use @Captioner in the chat to request the Captioner join your breakout."
      />,
      {
        autoClose: LONG_AUTO_CLOSE_DURATION,
        hideProgressBar: true,
        icon: (
          <ToastIconWithBorder
            Icon={
              <CaptionUnavailableIcon
                className="absolute-center"
                color="#B2261E"
                width={30}
                height={30}
                viewBox="0 0 30 30"
              />
            }
            borderColor="#B2261E"
          />
        ),
      },
    );
  }
  return toastWrapper(_createCaptionerIsInAnotherRoomToast)();
}

function createUserRaisedTheirHandToast(
  user: UserType,
  breakoutRoomTitle: string,
  visitGroupCallback: () => void,
  messageCallback: () => void,
) {
  function _createUserRaisedTheirHandsToast() {
    playNotificationAudio();
    return toast(
      <ToastBody
        title={`${longDisplayName(user)}`}
        text="Raised their hand"
        buttonText={`Visit Group ${breakoutRoomTitle}`}
        buttonCallback={visitGroupCallback}
        secondButtonText="Message"
        secondButtonCallback={messageCallback}
      />,
      {
        autoClose: LONG_AUTO_CLOSE_DURATION,
        hideProgressBar: true,
        icon: <ToastIconWithProfilePhoto user={user} />,
      },
    );
  }
  return toastWrapper(_createUserRaisedTheirHandsToast)(user.id, breakoutRoomTitle);
}

function createNoClassroomChangeToast(message?: string) {
  if (!message) return;
  createErrorToast("No Classroom Change", message, true);
}

export {
  ToastBody,
  ToastIconWithProfilePhoto,
  ToastIconWithBorder,
  createSharingNonScrollableResourceToast,
  createBreakoutEndingSoonToast,
  createErrorToast,
  createDeviceToast,
  createDeviceErrorToast,
  createFileUploadingToast,
  createScreenshareRequestToast,
  createFeatureResourceToast,
  createResourceUploadedToast,
  createMentionToast,
  createMentionToastWithVisitGroupCta,
  createRecordingToast,
  createPoorConnectionToast,
  createBugReportSubmittedToast,
  createNoCaptionsToast,
  createCaptionerIsInAnotherRoomToast,
  createUserRaisedTheirHandToast,
  createNoClassroomChangeToast,
};
